import React, { useState } from 'react';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import { RefreshTwoTone } from '@mui/icons-material';
import { Card, Alert, TextField, Typography, Grid, CircularProgress, IconButton, Box } from '@mui/material';

import SERVER_API_URL from '../../../config';
import { EMAIL_REGEX } from '../../../utils/constants';
import { EMAIL_EXCEPTION, REQUIRED_FIELD } from '../../../utils/exception';
import CustomButton from '../../../components/custom-button/CustomButton';

const LIGHT_GRAY = '#f7f7f7';

const StyledCard = styled(Card)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? '' : LIGHT_GRAY,
}));

export default function ContactForm() {
  const [disabled, setDisabled] = useState(false);

  const [formError, setFormError] = useState(null);
  const [formErrorSeverity, setFormErrorSeverity] = useState('error');

  const [name, setName] = useState();
  const [nameClicked, setNameClicked] = useState(false);
  const [formErrorOnName, setFormErrorOnName] = useState(false);
  const [helperTextOnName, setHelperTextOnName] = useState(null);

  const [email, setEmail] = useState();
  const [emailClicked, setEmailClicked] = useState(false);
  const [formErrorOnEmail, setFormErrorOnEmail] = useState(false);
  const [helperTextOnEmail, setHelperTextOnEmail] = useState(null);

  const [subject, setSubject] = useState();
  const [subjectClicked, setSubjectClicked] = useState(false);
  const [formErrorOnSubject, setFormErrorOnSubject] = useState(false);
  const [helperTextOnSubject, setHelperTextOnSubject] = useState(null);

  const [message, setMessage] = useState();
  const [messageClicked, setMessageClicked] = useState(false);
  const [formErrorOnMessage, setFormErrorOnMessage] = useState(false);
  const [helperTextOnMessage, setHelperTextOnMessage] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setDisabled(true);

    if (
      !formErrorOnName &&
      !formErrorOnEmail &&
      !formErrorOnSubject &&
      !formErrorOnMessage &&
      name &&
      email &&
      subject &&
      message
    ) {
      try {
        const response = await axios.post(`${SERVER_API_URL}/sendFeedback`, {
          name,
          email,
          subject,
          message,
        });
        if (response.status === 201) {
          setName('');
          setEmail('');
          setSubject('');
          setMessage('');

          setFormError('መልዕቶ ተልኮአል።');
          setFormErrorSeverity('success');
        }
      } catch (e) {
        if (e.response.status === 400) {
          setFormError(e.response.data.message);
          setFormErrorSeverity('error');
        }
      }
    } else {
      setFormError('እባክዎ ፎርሙን በአግባቡ ይሙሉ።');
      setFormErrorSeverity('error');
    }

    setDisabled(false);

    setTimeout(() => {
      setFormError('');
    }, 2000);

  };

  const handleFormValueBlur = (e) => {
    e.preventDefault();
    setDisabled(false);

    const { name, value } = e.target;
    switch (name) {
      case 'name':
        if (value.length === 0) {
          setNameClicked(false);
          setFormErrorOnName(nameClicked && value.length === 0);
          setHelperTextOnName(REQUIRED_FIELD);
        } else {
          setHelperTextOnName(null);
          setFormErrorOnName(false);
        }
        break;
      case 'email':
        if (value.length === 0) {
          setEmailClicked(false);
          setFormErrorOnEmail(emailClicked && value.length === 0);
          setHelperTextOnEmail(REQUIRED_FIELD);
        } else if (!EMAIL_REGEX.test(value)) {
          setFormErrorOnEmail(true);
          setHelperTextOnEmail(EMAIL_EXCEPTION);
        } else {
          setHelperTextOnEmail(null);
          setFormErrorOnEmail(false);
        }
        break;
      case 'subject':
        if (value.length === 0) {
          setSubjectClicked(false);
          setFormErrorOnSubject(subjectClicked && value.length === 0);
          setHelperTextOnSubject(REQUIRED_FIELD);
        } else {
          setHelperTextOnSubject(null);
          setFormErrorOnSubject(false);
        }
        break;
      case 'message':
        if (value.length === 0) {
          setMessageClicked(false);
          setFormErrorOnMessage(messageClicked && value.length === 0);
          setHelperTextOnMessage(REQUIRED_FIELD);
        } else {
          setHelperTextOnMessage(null);
          setFormErrorOnMessage(false);
        }
        break;
      default:
        break;
    }
  };


  const handleFormValueChange = (e) => {
    e.preventDefault();
    setDisabled(false);

    const { name, value } = e.target;
    switch (name) {
      case 'name':
        setName(value);
        setFormErrorOnName(false);
        setHelperTextOnName(null);
        break;
      case 'email':
        setEmail(value);
        setFormErrorOnEmail(false);
        setHelperTextOnEmail(null);
        break;
      case 'subject':
        setSubject(value);
        setFormErrorOnSubject(false);
        setHelperTextOnSubject(null);
        break;
      case 'message':
        setMessage(value);
        setFormErrorOnMessage(false);
        setHelperTextOnMessage(null);
        break;
      default:
        break;
    }
  };

  const handleClearForm = () => {
    setDisabled(false);

    setName('');
    setNameClicked(false);
    setFormErrorOnName(false);
    setHelperTextOnName(null);

    setEmail('');
    setEmailClicked(false);
    setFormErrorOnEmail(false);
    setHelperTextOnEmail(null);

    setSubject('');
    setSubjectClicked(false);
    setFormErrorOnSubject(false);
    setHelperTextOnSubject(null);

    setMessage('');
    setMessageClicked(false);
    setFormErrorOnMessage(false);
    setHelperTextOnMessage(null);
  };

  return (
    <>
      <StyledCard
        sx={{ p: { xs: 2, sm: 5 }, mb: { xs: 2, sm: 4, md: 6, lg: 8 } }}
        component="form"
        onSubmit={handleSubmit}
      >
        <Grid container spacing={3}>
          <Grid item xs={12} sm={12}>
            <Typography variant="h6" sx={{ p: 2, color: 'darkgray' }}>
              ለሰሜን ሸዋ ሃገረ-ስብከት የመረጃ አስተዳደር ስርዓት አስተዳደር እንዲደርስ የሚፈልጉትን መልዕክት ከታች ባለው ቦታ ያስቀምጡ።
            </Typography>
            {formError && (
              <Alert sx={{ width: '100%' }} severity={formErrorSeverity} onClose={() => setFormError('')}>
                {formError}
              </Alert>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              id="name"
              variant="outlined"
              name="name"
              label="ሙሉ ስም"
              placeholder="ሙሉ ስም"
              value={name}
              required
              fullWidth
              error={formErrorOnName}
              onChange={handleFormValueChange}
              helperText={formErrorOnName ? helperTextOnName : null}
              onFocus={() => setNameClicked(true)}
              onBlur={handleFormValueBlur}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              id="email"
              variant="outlined"
              name="email"
              label="ኢሜል ያስገቡ"
              placeholder="ኢሜል ያስገቡ"
              value={email}
              required
              fullWidth
              error={formErrorOnEmail}
              onChange={handleFormValueChange}
              helperText={formErrorOnEmail ? helperTextOnEmail : null}
              onFocus={() => setEmailClicked(true)}
              onBlur={handleFormValueBlur}
            />
          </Grid>
          <Grid item xs={12} sm={12}>
            <TextField
              id="subject"
              variant="outlined"
              name="subject"
              label="ርዕሰ-ጉዳይ ያስገቡ"
              placeholder="ርዕሰ-ጉዳይ ያስገቡ"
              fullWidth
              value={subject}
              required
              error={formErrorOnSubject}
              onChange={handleFormValueChange}
              helperText={formErrorOnSubject ? helperTextOnSubject : null}
              onFocus={() => setSubjectClicked(true)}
              onBlur={handleFormValueBlur}
            />
          </Grid>
          <Grid item xs={12} sm={12}>
            <TextField
              id="message"
              variant="outlined"
              name="message"
              label="መልዕክቶን ያስገቡ"
              rows={5}
              maxRows={10}
              placeholder="መልዕክቶን ያስገቡ"
              fullWidth
              multiline
              value={message}
              required
              error={formErrorOnMessage}
              onChange={handleFormValueChange}
              helperText={formErrorOnMessage ? helperTextOnMessage : null}
              onFocus={() => setMessageClicked(true)}
              onBlur={handleFormValueBlur}
            />
          </Grid>
          <Grid item xs={12} sm={12}>
            <Box sx={{ display: 'flex', justifyContent: 'right' }}>
              <Box sx={{ display: 'flex', gap: 1, width: { xs: '50%', sm: '40%', md: '30%', lg: '40%' } }}>
                <IconButton onClick={handleClearForm}>
                  <RefreshTwoTone />
                </IconButton>
                <CustomButton fullWidth size="large" type="submit" variant="contained" disabled={disabled}>
                  {!disabled && <>አስቀምጥ</>}
                  {disabled && (
                    <>
                      <CircularProgress style={{ width: '20px', height: '20px' }} />  ይጠብቁ...
                    </>
                  )}
                </CustomButton>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </StyledCard>
    </>
  );
}
